import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { TOPIC_TYPES } from "../../../utils/constants/topicTypes";
import PushNotificationDTO from "../../../shared/dtos/pushNotificationDTO";
import { NotificationRecurrenceModal, RecurrenceRule } from "./NotificationRecurrenceModal";
import { InfoCircleOutlined, CalendarOutlined, TeamOutlined } from "@ant-design/icons";
import { UserSearchSelect, DriverSearchSelect } from "../../common/UserSearchSelect";
import { GetUserById } from "../../../services/userAPI";
import { getProviderById } from "../../../services/adminProviderApi";


const { Text } = Typography;

interface NotificationParams extends PushNotificationDTO {
  recurrenceRule?: RecurrenceRule | null;
}

type PushNotificationFormProps = {
  form: FormInstance<NotificationParams>;
  formLoading: boolean;
  notification: PushNotificationDTO | null;
  handleUpsert: (data?: PushNotificationDTO) => Promise<void>;
  handleCancel: () => void;
};

dayjs.extend(utc);
dayjs.extend(timezone);

export const PushNotificationForm = ({
  form,
  formLoading,
  notification,
  handleUpsert,
  handleCancel,
}: PushNotificationFormProps) => {
  const [recurrenceModalOpen, setRecurrenceModalOpen] = useState(false);
  const topic = Form.useWatch('topic', form);
  const recurrenceRule = Form.useWatch('recurrenceRule', form);

  const browserTimezone = dayjs.tz.guess();


  React.useEffect(() => {
    const fetchLabels = async () => {
      if (!notification) return;

      const fetchSet = async (ids: string[] | undefined, fetchFn: (id: number) => Promise<any>) => {
        if (!ids?.length) return [];
        
        const fetchPromises = ids.map(async (id) => {
          try {
            const data = await fetchFn(Number(id));
            if (data) {
              return { 
                label: `${data.firstName} ${data.lastName} (${data.email || data.mobile || 'S/D'})`, 
                value: String(id) 
              };
            }
            return { label: `ID: ${id}`, value: String(id) };
          } catch (e) {
            return { label: `ID: ${id}`, value: String(id) };
          }
        });

        return await Promise.all(fetchPromises);
      };


      const userIds = await fetchSet(notification.targetUserIds, GetUserById);
      const driverIds = await fetchSet(notification.targetDriverIds, getProviderById);


      form.setFieldsValue({
        targetUserIds: userIds as any,
        targetDriverIds: driverIds as any,
      });
    };

    fetchLabels();
  }, [notification, form]);

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          const data: any = {
            ...values,
            targetUserIds: values.targetUserIds?.map((item: any) => item.value || item),
            targetDriverIds: values.targetDriverIds?.map((item: any) => item.value || item),
            timezone: browserTimezone,
            scheduledAtLocal: values.scheduledAt && dayjs.isDayjs(values.scheduledAt)
              ? (values.scheduledAt as dayjs.Dayjs).format("YYYY-MM-DD HH:mm:ss")
              : undefined,
            scheduledAt: values.scheduledAt && dayjs.isDayjs(values.scheduledAt)
              ? dayjs.tz(values.scheduledAt.format("YYYY-MM-DD HH:mm:ss"), browserTimezone).utc().format()
              : undefined,
          };
          handleUpsert(data);
        }}

        colon={false}
        initialValues={{
          topic: notification?.topic,
          recurrenceRule: notification?.recurrenceRule
        }}
      >
        <Form.Item
          name="title"
          label="Título"
          rules={[{ required: true, message: "Campo requerido" }]}
        >
          <Input type="text" placeholder="Título de la notificación" />
        </Form.Item>
        <Form.Item
          name="body"
          label="Descripcion"
          rules={[{ required: true, message: "Campo requerido" }]}
        >
          <Input.TextArea rows={3} placeholder="Descripción de la notificación" />
        </Form.Item>
        <Form.Item
          name="topic"
          label="Enviar a"
          rules={[{ required: true, message: "Campo requerido" }]}
        >
          <Select
            size="middle"
            options={TOPIC_TYPES}
            optionFilterProp="label"
            showSearch
          />
        </Form.Item>

        {topic === "mowi-specific" && (
          <Row>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item name="targetUserIds" label={<Space><TeamOutlined /> Buscar Usuarios</Space>} tooltip="Busque por nombre, email o teléfono">
                <UserSearchSelect mode="multiple" />
              </Form.Item>
              <Form.Item name="targetDriverIds" label={<Space><TeamOutlined /> Buscar Conductores</Space>} tooltip="Busque por nombre, email o teléfono">
                <DriverSearchSelect mode="multiple" />
              </Form.Item>

            </Space>
          </Row>
        )}

        <Form.Item name="url" label="Enlace">
          <Input type="text" placeholder="https://mowiapp.com/" />
        </Form.Item>

        <Row align="bottom">
          <Space align="start" size="large">
            <Form.Item name="scheduledAt" label="Agendar" style={{ marginBottom: 0 }}>
              <DatePicker
                showTime
                placeholder="Seleccione una fecha"
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: 220 }}
              />
            </Form.Item>

            <Form.Item label="Repetición">
              <Button
                icon={<CalendarOutlined />}
                onClick={() => setRecurrenceModalOpen(true)}
                type={recurrenceRule ? "primary" : "default"}
                ghost={!!recurrenceRule}
              >
                {recurrenceRule ? "Repetición Activa" : "Configurar repetición"}
              </Button>
            </Form.Item>
          </Space>
        </Row>

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <InfoCircleOutlined /> Zona horaria: <strong>{browserTimezone}</strong>
          </Text>
        </div>

        <Form.Item name="recurrenceRule" hidden>
          <Input />
        </Form.Item>
        <Row justify="end">
          <Space>
            <Button type="primary" htmlType="submit" loading={formLoading}>
              {notification ? "Actualizar" : "Crear"} notificacion
            </Button>
            <Button type="default" onClick={handleCancel} disabled={formLoading}>
              Cancelar
            </Button>
          </Space>
        </Row>
      </Form>

      <NotificationRecurrenceModal
        open={recurrenceModalOpen}
        onCancel={() => setRecurrenceModalOpen(false)}
        initialRule={recurrenceRule}
        defaultScheduledAt={form.getFieldValue('scheduledAt' as any)}
        onSave={(rule) => form.setFieldValue('recurrenceRule' as any, rule)}
      />
    </>
  );
};

export default PushNotificationForm;
